import { nonnull } from "@tento-core/assert";
import "@tento-ui/components/event-details/fields/tag-selector";
import type { AuthScopedCalendarDisplay, EventCategories, EventDraft, EventsClient, NewEventTimeRange } from "@tento-lona";
import type { EventItem, Json, Option } from "@tento-lona/sheets";
import {
  AuthScopedCalendarId,
  EventWriteError,
  EventCategory,
  EventCategoryItem,
} from "@tento-lona";
import { Network } from "@tento-lona/network";
import { Actor } from "@tento-lona/sheets";

import { Anchor, AnchorInstance } from "@tento-ui/components/anchor";
import { EventDetailsContent } from "@tento-ui/components/event-details/event-details-content";
import { EventTime } from "@tento-ui/components/event-details/event-time";
import { EventDetailsFieldAddParticipants } from "@tento-ui/components/event-details/fields/add-participants";
import { EventDetailsFieldAllDay } from "@tento-ui/components/event-details/fields/all-day";
import { EventDetailsFieldLocation } from "@tento-ui/components/event-details/fields/location";
import { EventDetailsFieldRecurrenceInput } from "@tento-ui/components/event-details/fields/recurrence-input";
import { EventDetailsFieldRichText } from "@tento-ui/components/event-details/fields/rich-text";
import { EventDetailsFieldTagSelector } from "@tento-ui/components/event-details/fields/tag-selector";
import { EventDetailsFieldTimezone } from "@tento-ui/components/event-details/fields/timezone";
import { EventDetailsFieldVideoConferencing } from "@tento-ui/components/event-details/fields/video-conferencing";
import { Position } from "@tento-ui/anchor-positioning-utils";
import { Round } from "@tento-ui/components/round";
import { Toast } from "@tento-ui/components/toast/toast";
import { $$ } from "@tento-ui/fastdom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Tracing } from "@tento-core/tracing";
import {
  DateTime,
  DateTimeRegion,
  NaiveDate,
  RRule,
  TimeOfDay,
  TimezoneRegion,
  Utc,
} from "@tento-chrono";
import { RecurrenceModel } from "@tento-ui/components/recurrence-editor";
import { CalendarListRow } from "./calendar-list.ts";
import { CalendarPicker } from "./calendar-picker.ts";
import { EventTagList } from "./event-context-menu.ts";
import { EventWriteRefusal } from "./event-write-refusal.ts";

/**
 * A category as tento-ui's tag selector reads one: its `color()` always
 * answers a colour (the category's in its client's theme, else the gray),
 * so the fallback is written here, where the selector is fed.
 */
type CategoryTag = {
  readonly name: EventCategory;
  readonly emoji: string;
  readonly item: EventCategoryItem;
  color(): string;
  colorDimmed(): string;
};

function categoryTag(item: EventCategoryItem): CategoryTag {
  return {
    name: item.name,
    emoji: item.emoji,
    item,
    color: () => item.colorOpt() ?? "var(--gray)",
    colorDimmed: () => item.colorDimmedOpt() ?? "var(--gray)",
  };
}

/**
 * The editable fields of an event draft (title, time, calendar, category) and
 * their state.
 */
export class EventFields {
  #state: Option<EventFields.State> = null;
  #delegate: Option<EventFields.Delegate> = null;

  #categoryDecisionSet = false;
  #useSuggestedCategory: Option<boolean> = null;
  #manualCategory: Option<EventCategoryItem> = null;
  #suggestedCategory: Option<EventCategoryItem> = null;
  /** The client's event categories (`client.events.categories`), set by `bind`. */
  #categories: Option<EventCategories> = null;
  /** The last bind's access: a later category change re-binds the tags with it, never as editable. */
  #editable = true;

  #$calendarPopover: Option<AnchorInstance> = null;
  $fields = {
    $allDay: EventDetailsFieldAllDay.make(),
    $description: EventDetailsFieldRichText.make(),
    $addParticipants: EventDetailsFieldAddParticipants.make(),
    $tags: EventDetailsFieldTagSelector.make<CategoryTag>(
      (tag) => {
        this.setUserCategory(tag.item);
      },
      (onSelect) =>
        EventTagList.makeWith(
          nonnull(this.#categories, "EventFields: tag list opened before bind"),
          (item) => onSelect(categoryTag(item)),
        ),
      null,
      "Add tags",
    ),
    $calendar: EventDetailsContent.Field.makeWith(CalendarListRow.make(), {
      padding: "12px",
    }),
    $recurrence: EventDetailsFieldRecurrenceInput.make(),
    $videoConferencing: EventDetailsFieldVideoConferencing.make(),
    $timezone: EventDetailsFieldTimezone.make(),
    $location: EventDetailsFieldLocation.make(),
  };

  defaultFieldOrder: (keyof typeof this.$fields)[] = [
    "$allDay",
    "$description",
    "$addParticipants",
    "$tags",
    "$calendar",
    "$recurrence",
    "$videoConferencing",
    "$timezone",
    "$location",
  ];

  constructor() {
    this.$fields.$allDay.id = "all-day";
    this.$fields.$description.id = "description";
    this.$fields.$addParticipants.id = "add-participants";
    this.$fields.$tags.id = "tags";
    this.$fields.$calendar.id = "calendar";
    this.$fields.$calendar.assignStyles({
      display: "flex",
      minHeight: "36px",
      position: "relative",
      alignItems: "center",
    });
    this.$fields.$calendar.$content.assignStyles({
      "--cell-height": "12px",
    });
    this.$fields.$recurrence.id = "recurrence";
    this.$fields.$videoConferencing.id = "video-conferencing";
    this.$fields.$tags.$content.setSourceToggleHandler(() =>
      this.#toggleCategorySource()
    );

    {
      // _) Location - set up callback
      this.$fields.$location.$content.controller.onEditingFinished = () => {
        if (this.#state)
          this.#state.location = this.$fields.$location.$content.controller.text ?? null;
        this.#notify(new Set(["location"]));
      };
    }
  }

  bindDelegate(delegate: EventFields.Delegate) {
    this.#delegate = delegate;
  }

  /** Whether this form has a local category decision this session. */
  get categoryUserSet(): boolean {
    return this.#categoryDecisionSet;
  }

  /** Reset local category slots at the start of a new create/details session. */
  resetCategoryUserSet(): void {
    this.#categoryDecisionSet = false;
    this.#useSuggestedCategory = null;
    this.#manualCategory = null;
    this.#suggestedCategory = null;
  }

  #resolvedUseSuggestedCategory(): boolean {
    if (this.#useSuggestedCategory != null) return this.#useSuggestedCategory;
    // On by default: the toggle is armed whenever there is no manual category,
    // even before the classifier has produced a suggestion (the star shows
    // "ready"). Picking a manual category, or explicitly toggling off, wins.
    return this.#manualCategory == null;
  }

  #displayedCategory(): Option<EventCategoryItem> {
    return this.#resolvedUseSuggestedCategory()
      ? this.#suggestedCategory
      : this.#manualCategory;
  }

  selectedCategory(): Option<EventCategoryItem> {
    return this.#displayedCategory();
  }

  #categorySource(
    item: Option<EventCategoryItem>,
  ): EventFields.CategorySource {
    if (this.#resolvedUseSuggestedCategory()) return "suggested";
    return item ? "manual" : "none";
  }

  #categoryStarState(): EventFields.CategoryStarState {
    if (!this.#resolvedUseSuggestedCategory()) return "off";
    return this.#suggestedCategory ? "suggesting" : "ready";
  }

  #bindCategoryDisplay(editable: boolean): void {
    const item = this.#displayedCategory();
    const source = this.#categorySource(item);
    if (this.#state) {
      this.#state.category = item?.name ?? null;
      this.#state.categorySource = source;
      this.#state.manualCategory = this.#manualCategory?.name ?? null;
      this.#state.suggestedCategory = this.#suggestedCategory?.name ?? null;
      this.#state.useSuggestedCategory = this.#useSuggestedCategory;
    }
    this.$fields.$tags.$content.bindWithSource(
      item == null ? null : categoryTag(item),
      editable,
      source,
      this.#categoryStarState(),
    );
  }

  #toggleCategorySource(): void {
    if (!this.#state || !this.#editable) return;
    this.#categoryDecisionSet = true;
    this.#useSuggestedCategory = !this.#resolvedUseSuggestedCategory();
    this.#bindCategoryDisplay(true);
    this.#notify(new Set(["category"]));
  }

  /**
   * Apply a classifier suggestion to its own slot. The visible tag only changes
   * when the resolved decision is currently using suggestions.
   */
  setSuggestedCategory(item: Option<EventCategoryItem>): boolean {
    if (!this.#state) return false;
    const previousItem = this.#displayedCategory();
    const previous = previousItem?.name ?? null;
    const previousSource = this.#categorySource(previousItem);
    this.#suggestedCategory = item;
    this.#state.suggestedCategory = item?.name ?? null;
    const nextItem = this.#displayedCategory();
    const next = nextItem?.name ?? null;
    const nextSource = this.#categorySource(nextItem);
    $$.mutate(() => this.#bindCategoryDisplay(this.#editable));
    return previous !== next || previousSource !== nextSource;
  }

  setUserCategory(item: Option<EventCategoryItem>): void {
    this.#categoryDecisionSet = true;
    this.#manualCategory = item;
    this.#useSuggestedCategory = false;
    if (this.#state) {
      this.#state.manualCategory = item?.name ?? null;
      this.#state.useSuggestedCategory = false;
    }
    $$.mutate(() => this.#bindCategoryDisplay(this.#editable));
    this.#notify(new Set(["category"]));
  }

  dispose() {
    GESTURE_MANAGER.unregister(this.$fields.$calendar.$content);
    GESTURE_MANAGER.unregister(this.$fields.$calendar);
  }

  #notify(changedFields: Set<EventFields.StateField>) {
    if (!this.#state) return;
    this.#delegate?.onUpdate(this.#state, changedFields);
  }

  bind(
    state: EventFields.State,
    modalContext: string,
    viewerTz: TimezoneRegion,
    ucals: readonly Network.CalendarList[],
    categories: EventCategories,
    access: "editable" | "readonly" = "editable",
  ) {
    const editable = access === "editable";
    this.#editable = editable;
    this.#state = state;
    this.#categories = categories;
    this.#useSuggestedCategory = state.useSuggestedCategory ?? null;
    this.#categoryDecisionSet = this.#useSuggestedCategory != null;
    const legacySource = state.categorySource ??
      (state.category ? "manual" : "none");
    this.#manualCategory = categories.itemOpt(
      ((state.manualCategory ??
        (legacySource === "manual" ? state.category : null)) as Option<
          EventCategory
        >) ?? null,
    );
    this.#suggestedCategory = categories.itemOpt(
      ((state.suggestedCategory ??
        (legacySource === "suggested" ? state.category : null)) as Option<
          EventCategory
        >) ?? null,
    );

    {
      // _) Bind all-day toggle
      this.$fields.$allDay.$content.bind((enabled) => {
        if (this.#state) this.#state.isAllDay = enabled;
        this.#notify(new Set(["isAllDay"]));
      }, state.isAllDay);
      this.$fields.$allDay.$content.setInteractive(editable);
    }

    {
      // _) Bind description with existing data
      this.$fields.$description.$content.bind({
        html: state.description,
        placeholder: "Description",
        editable,
        onChange: (description) => {
          if (this.#state) this.#state.description = description;
          this.#notify(new Set(["description"]));
        },
      });
      this.$fields.$description.style.overflow = "hidden";
    }

    {
      // _) Bind location with existing data
      this.$fields.$location.$content.bind(state.location, "Add a location");
    }

    {
      // _) Bind tags with existing category
      this.#bindCategoryDisplay(editable);
    }

    {
      // _) Bind participants with existing invitees
      this.$fields.$addParticipants.$content.bind({
        onEmailsChange: (inviteeEmails) => {
          if (this.#state) this.#state.inviteeEmails = inviteeEmails;
          this.#notify(new Set(["inviteeEmails"]));
        },
        initialEmails: state.inviteeEmails,
        editable,
      });
    }

    {
      // _) Bind video conferencing with existing state. The field only
      // shows when the calendar supports conference links (per its
      // server-computed capabilities; absent capabilities fall back to
      // Meet — the pre-capability behavior).
      const conference = conferenceProviderFor(state.acal[1]);
      this.$fields.$videoConferencing.style.display = conference ? "" : "none";
      const hasConferenceData = state.conferenceData != null;
      this.$fields.$videoConferencing.$content.bind((enabled) => {
        if (this.#state && conference) {
          this.#state.conferenceOperation = enabled
            ? { action: "create" as const, kind: conference }
            : { action: "delete" as const };
        }
        this.#notify(new Set(["conferenceOperation"]));
      }, hasConferenceData);
      this.$fields.$videoConferencing.$content.setInteractive(editable);
    }

    {
      const dt = viewerTz.toTz(state.time.start);
      const referenceDate = dt.date;
      // Parse the event's existing RRULE (master rule, copied onto child
      // instances) back into the editor model so the field shows the actual
      // rule ("Weekly", ...) instead of the "Does not repeat" placeholder.
      const existingModel: Option<RecurrenceModel> = (() => {
        const rec = state.recurrence;
        if (!rec || rec.length === 0) return null;
        const parsed = RRule.parseWithRecurrence(rec);
        return parsed.isOk
          ? RecurrenceModel.fromRRule(nonnull(parsed.asOk(), "EventFields: parsed rule"))
          : null;
      })();
      this.$fields.$recurrence.$content.bind({
        referenceDate,
        model: existingModel,
        onChange: (model) => {
          if (this.#state) {
            if (model) {
              const rruleString = this.$fields.$recurrence.$content
                .modelToRRule(model)
                .toString();
              this.#state.recurrence = [rruleString];
            } else {
              this.#state.recurrence = null;
            }
          }
          this.#notify(new Set(["recurrence"]));
        },
        placeholder: "Does not repeat",
        showChevron: false,
        context: modalContext,
        editable,
      });
    }

    {
      // _) Locations
      if (editable) this.$fields.$location.$content.enableEditing();
      else this.$fields.$location.$content.disableEditing();
    }

    {
      // _) Calendar
      const $calendar = this.$fields.$calendar.$content;
      $calendar.bind(state.acal);
      const onClick = () => {
        if (this.#$calendarPopover != null) {
          this.#$calendarPopover.dismiss();
          this.#$calendarPopover = null;
          return;
        }
        const $picker = CalendarPicker.makeWith({
          ucals,
          selected: AuthScopedCalendarId.of(state.acal),
          onSelect: (acal) => {
            // Write through `this.state`, not the `bind()`-time `state`
            // capture: a re-bind (e.g. `CreateEventForm.bindTime`) replaces the
            // state object, and this click handler outlives that swap.
            if (this.#state) this.#state.acal = acal;
            this.$fields.$calendar.$content.bind(acal);
            this.#$calendarPopover?.dismiss();
            // Announce the pick like every other field does. Without this the
            // picker mutated the draft in place and no delegate ever heard
            // about it, so the Edit panel's Save bar never appeared and the
            // chosen calendar was gone on reload.
            this.#notify(new Set(["acal"]));
          },
          showEditableOnly: true,
          ...(this.#delegate?.calendarWritability != null ? { writability: this.#delegate.calendarWritability } : {}),
        });
        $picker.focus();
        $picker.assignStyles({
          maxHeight: "480px",
          overflowY: "auto",
          width: "100%",
        });

        const anchorWidth = this.$fields.$calendar.getBoundingClientRect().width;
        const $container = Round.makeWithContent($picker, {
          bg: "var(--bg-elevated)",
          radius: 8,
          shadow: "var(--box-shadow)",
        }).assignStyles({
          boxSizing: "border-box",
          height: "min-content",
          maxWidth: "calc(100vw - 24px)",
          padding: "8px",
          width: `${anchorWidth}px`,
        });

        // The create-form field container clips its overflow, so an
        // anchor-relative popover gets cut off. Lift overflow to visible while
        // the picker is open and restore it on dismiss.
        const $fieldContainer = this.$fields.$calendar.parentElement ??
          this.$fields.$calendar;
        const previousOverflow = $fieldContainer.style.overflow;
        $fieldContainer.style.overflow = "visible";

        this.#$calendarPopover = Anchor.reveal({
          $anchor: this.$fields.$calendar,
          $content: $container,
          escapeContainer: false,
          positionRelativeTo: "anchor",
          position: Position.BOTTOM_LEFT_X,
          offset: {
            outer: {
              vertical: 0,
              horizontal: 0,
            },
          },
          onDismiss: () => {
            $fieldContainer.style.overflow = previousOverflow;
            this.#$calendarPopover = null;
          },
        });
      };
      // A read-only event's calendar is shown, not picked: no picker, no
      // pointer, and a picker a previous editable bind opened is closed.
      if (editable) {
        $calendar.style.cursor = "pointer";
        this.$fields.$calendar.style.cursor = "pointer";
        GESTURE_MANAGER.addPointerEvent(this.$fields.$calendar, {
          onClick,
        });
      } else {
        this.#$calendarPopover?.dismiss();
        this.#$calendarPopover = null;
        $calendar.style.cursor = "";
        this.$fields.$calendar.style.cursor = "";
        GESTURE_MANAGER.unregister(this.$fields.$calendar);
      }
    }

    {
      // _) Timezone
      const isFlightEnabled = state.flightTimezone != null;
      this.$fields.$timezone.$content.bind(
        {
          onInfoClick: () => this.#delegate?.onShowFlightInfo?.(),
          onEnableTimezone: (on) => {
            // When turning OFF, clear the flight timezone and notify
            if (!on && this.#state) {
              this.#state.flightTimezone = null;
              this.#state.endTz = this.#state.startTz ?? null;
              this.#notify(new Set(["timezone"]));
            }
            // When turning ON, the modal will handle the update via onUpdate
          },
          onShowModal: () => {
            return new EventTime(
              EventFields.dtrr(nonnull(this.#state, "EventFields: time modal before bind")),
              false,
            );
          },
          onUpdate: (eventTime: EventTime) => {
            if (this.#state) {
              const utcTime = new DateTime.Range(
                eventTime.range.start.asWallClock(),
                eventTime.range.end.asWallClock(),
              ).toUtc();
              this.#state.time = utcTime;
              this.#state.flightTimezone = eventTime.range.end.tz.fullname;
              this.#state.endTz = eventTime.range.end.tz;
            }
            this.#notify(new Set(["time", "timezone"]));
          },
        },
        modalContext,
        isFlightEnabled,
      );
      this.$fields.$timezone.$content.setInteractive(editable);
    }
  }
}

/**
 * Types and values that go with `EventFields`: `draftState`, `CategorySource`,
 * `CategoryStarState`, `StateField`, `State`, `Delegate`, and 5 more.
 */
export namespace EventFields {
  /**
   * A new draft's `State`: the calendar, the time, whether it is all-day and
   * its start zone, with every editor field `null` (none yet) — spread it and
   * state the fields the draft starts with.
   */
  export function draftState(init: {
    readonly acal: AuthScopedCalendarDisplay;
    readonly time: DateTime.Range<Utc>;
    readonly isAllDay: boolean;
    readonly startTz: Option<TimezoneRegion>;
  }): State {
    return {
      acal: init.acal,
      time: init.time,
      isAllDay: init.isAllDay,
      conferenceData: null,
      startTz: init.startTz,
      endTz: null,
      allDayDateRange: null,
      title: null,
      description: null,
      category: null,
      categorySource: null,
      manualCategory: null,
      suggestedCategory: null,
      useSuggestedCategory: null,
      recurrence: null,
      location: null,
      inviteeEmails: null,
      conferenceOperation: null,
      flightTimezone: null,
    };
  }

  export type CategorySource = "none" | "suggested" | "manual";
  export type CategoryStarState = "off" | "ready" | "suggesting";

  export type StateField =
    /** The event's calendar. Not a `Network.EventUpdate` field — a calendar
     *  change commits as a move (or copy+delete), so consumers handle it
     *  separately from the patch fields below. */
    | "acal"
    | "title"
    | "description"
    | "category"
    | "recurrence"
    | "location"
    | "inviteeEmails"
    | "time"
    | "conferenceOperation"
    | "timezone"
    | "isAllDay";

  export type State = {
    acal: AuthScopedCalendarDisplay;
    time: DateTime.Range<Utc>;
    isAllDay: boolean;
    /** Read-only: the actual conference data from Google (`null`: none). */
    conferenceData: Option<Json>;
    // The editor's fields: always stated, `null` when the event has none
    // (or the user cleared it).
    title: Option<string>;
    description: Option<string>;
    category: Option<string>;
    categorySource: Option<CategorySource>;
    manualCategory: Option<string>;
    suggestedCategory: Option<string>;
    useSuggestedCategory: Option<boolean>;
    recurrence: Option<string[]>;
    location: Option<string>;
    inviteeEmails: Option<string[]>;
    conferenceOperation: Option<Network.ConferenceOperation>; // What action to perform

    flightTimezone: Option<string>;
    startTz: Option<TimezoneRegion>;
    endTz: Option<TimezoneRegion>;
    allDayDateRange: Option<NaiveDate.Range>;
  };

  export type Delegate = {
    onUpdate: (state: State, changedFields: Set<StateField>) => void;
    onShowFlightInfo?: () => void;
    /** Whether a calendar takes writes, as the client knows it (`EventsClient.peekCalendarWritability`): the picker leaves out one it knows is read-only. */
    calendarWritability?: (calendar: AuthScopedCalendarDisplay) => Option<EventsClient.Writability>;
  };

  export function dtrr(state: EventFields.State): DateTimeRegion.Range {
    const start = state.time.start;
    const end = state.time.end;
    const startTz = nonnull(state.startTz, "EventFields.dtrr: draft has no start zone");
    if (state.endTz) {
      return new DateTimeRegion.Range(
        new DateTimeRegion(startTz.toTz(start).ndt, startTz),
        new DateTimeRegion(state.endTz.toTz(end).ndt, state.endTz),
      );
    } else {
      return DateTimeRegion.Range.fromSingleTz(new DateTime.Range(start, end), startTz);
    }
  }

  /**
   * Build a display `DateTimeRegion.Range` for the ALL-DAY representation from
   * `allDayDateRange` (inclusive), in the draft's timezone — the same whole-day
   * window `dtrr` would produce, WITHOUT mutating `state.time`. This is what
   * keeps the partial and all-day representations independent.
   */
  export function allDayDtrr(
    state: EventFields.State,
    range: NaiveDate.Range,
  ): DateTimeRegion.Range {
    const tz = nonnull(state.startTz, "EventFields.allDayDtrr: draft has no start zone");
    const start = tz.toWallClock(range.start.withTime(TimeOfDay.ZERO));
    const end = tz.toWallClock(range.end.addDays(1).withTime(TimeOfDay.ZERO));
    return DateTimeRegion.Range.fromSingleTz(
      new DateTime.Range(start, end).toUtc(),
      tz,
    );
  }

  /**
   * The `EventTime` for the ACTIVE representation: the all-day span when
   * `isAllDay` (from `allDayDateRange`), otherwise the timed range (from
   * `time`). The single source for every time-display bind — so toggling
   * `isAllDay` shows the right representation without overwriting the other.
   */
  export function selectedEventTime(state: EventFields.State): EventTime {
    if (state.isAllDay && state.allDayDateRange) {
      return new EventTime(allDayDtrr(state, state.allDayDateRange), true);
    }
    return new EventTime(dtrr(state), state.isAllDay);
  }

  /**
   * The inclusive day span of a timed range, in the given timezone. This is the
   * default all-day span allocated on the first toggle to all-day, and the
   * fallback the all-day representation reads before any explicit resize — the
   * one place that "timed range → days" is derived.
   */
  export function timedRangeDays(
    time: DateTime.Range<Utc>,
    tz: TimezoneRegion,
  ): NaiveDate.Range {
    return new NaiveDate.Range(tz.toTz(time.start).date, tz.toTz(time.end).date);
  }

  /**
   * Create the draft `state` describes. A refusal of a read-only calendar
   * (`EventWriteRefusal.isReadOnly`, the SDK's up-front refusal or the
   * server's) goes to `report` with the draft's calendar, as every other
   * verb's refusal goes to its `EventWriteRefusal.Report`; any other failure
   * is toasted here.
   */
  export async function createEvent(
    state: EventFields.State,
    sdkEvents: Pick<EventsClient, "create">,
    report: EventWriteRefusal.CalendarReport,
  ) {
    // `sdkEvents` routes writes through the canonical events row (always
    // attached to EventsTable) — works for virtual leaf rows that have
    // no `_rowHandle` of their own.

    const conferenceData: Option<Network.ConferenceOperation> =
      state.conferenceOperation?.action === "create"
        ? { action: "create", kind: state.conferenceOperation.kind }
        : null;

    // The selector is `isAllDay` ALONE: with the dual representation, an
    // allocated `allDayDateRange` no longer implies an all-day event — it's the
    // (independent) all-day span the draft carries alongside its timed `time`.
    // Committing persists the selected representation and discards the other.
    const createAsAllDay = state.isAllDay;
    const startTz = nonnull(state.startTz, "EventFields: draft has no start zone");
    const startDateFromTime = startTz.toTz(state.time.start).date;
    const endDateFromTime = startTz.toTz(state.time.end).date;

    // `allDayDateRange` is stored INCLUSIVE; the draft's `endDate` is EXCLUSIVE
    // (the day after the last covered day), so add a day.
    const when: NewEventTimeRange = createAsAllDay
      ? {
        startDate: state.allDayDateRange?.start ?? startDateFromTime,
        endDate: (state.allDayDateRange?.end ?? endDateFromTime).addDays(1),
      }
      : { start: state.time.start, end: state.time.end };

    const draft: EventDraft = {
      when,
      category: state.manualCategory ?? null,
      conferenceData: conferenceData ?? null,
      description: state.description ?? null,
      inviteeEmails: state.inviteeEmails ?? null,
      location: state.location ?? null,
      recurrence: state.recurrence ?? null,
      suggestedCategory: state.suggestedCategory ?? null,
      timezone: state.flightTimezone ?? null,
      // The event's own IANA zone (the one being viewed/created in). Google
      // requires it on recurring events to expand the RRULE; distinct from the
      // flight `timezone` above. If absent, the connector defaults to UTC.
      eventTimezone: state.startTz?.fullname ?? null,
      title: state.title ?? null,
      useSuggestedCategory: state.useSuggestedCategory ?? null,
    };
    // `create` reconciles the placeholder itself, atomically with the
    // canonical ingest (including the mid-flight-deletion case) — see
    // `EventOps.create`. Checking `has(optimistic.id)` here after the
    // await misreads the ingest's same-date eviction as a user delete.
    // Owns the placeholder and its rollback; rejects with EventWriteError.
    const created: Promise<EventItem> = sdkEvents.create(
      state.acal,
      draft,
      Actor.EXTERNAL,
    );
    created
      .then((event) => {
        Tracing.info("ui")("created new event", event);
      })
      .catch((caught: unknown) => {
        Tracing.err("ui")("failed to create event", caught);
        const error = caught instanceof Error ? caught : new Error(String(caught));
        if (EventWriteRefusal.isReadOnly(error)) {
          report(state.acal, "create the event", error);
          return;
        }
        // A deleted/inaccessible calendar surfaces as `reason=calendar_gone`
        // (the server remaps the 404). Show a specific message; the background
        // reconcile drops the stale calendar from the picker on the next refresh.
        const reason = error instanceof EventWriteError ? error.reason : "";
        const errorText = `${reason} ${error.message}`;
        if (
          errorText.includes("calendar_gone") ||
          errorText.includes("no longer exists")
        ) {
          Toast.error("This calendar no longer exists", {
            description: "It may have been deleted. Choose a different calendar.",
          });
        } else {
          Toast.error("Failed to create event", {
            description: "Check that this calendar is connected with write permissions.",
          });
        }
      });
  }
}

/** Conference-link provider this calendar can attach, if any. Absent
 *  capability data (older server) falls back to Meet — the
 *  pre-capability behavior; an unrecognized provider string reads as
 *  unsupported rather than crashing the typed operation. */
function conferenceProviderFor(
  cald: Network.CalendarDisplay,
): Option<Network.ConferenceProvider> {
  if (!cald.capabilities) return "google_meet";
  return cald.capabilities.conference === "google_meet" ? "google_meet" : null;
}
